import React from 'react'

import ohMarkWhite from 'assets/images/teamroster/oh_mark_white.png'
import ohWordmarkOrange from 'assets/images/teamroster/openhouse_orange.png'

/* ------------------------------ ID card ------------------------------ */
// Ported from the standalone make_cards.py generator, which was measured off
// the Figma master (node 2130-457): 3236x5118 canvas, positions as fractions.
// Rendered here at quarter scale; the PDF captures at 2x for print.

const CARD_W = 809
const CARD_H = 1280
// mm page preserving the template's aspect ratio (vendor prints ~54x86 card).
const CARD_MM = [54, 85.4]

const CARD_ORANGE = '#EF5D34'
const CARD_DARK = '#212121'

// Orange top block's curved bottom edge, traced off the Figma template
// (x fraction, y fraction).
const CARD_WAVE = [
  [0.0, 0.29],
  [0.1, 0.296],
  [0.2, 0.303],
  [0.3, 0.31],
  [0.4, 0.318],
  [0.5, 0.327],
  [0.6, 0.334],
  [0.7, 0.338],
  [0.78, 0.34],
  [0.85, 0.339],
  [0.92, 0.336],
  [1.0, 0.33]
]

const CARD_LEGAL =
  'This ID card, issued by Openhouse, is exclusive to the educator it is ' +
  'assigned to and must be carried during all times within our hubs. ' +
  'Educators are required to immediately report a lost ID card to Openhouse. ' +
  'Any misuse or unauthorized use of this ID could lead to disciplinary ' +
  'actions. The card is strictly non-transferable. If found, we kindly ask ' +
  'you to return this card to the nearest Openhouse hub.'

// Emergency-contact names arrive ALL-CAPS or all-lowercase from the form —
// proper-case those, but leave already mixed-case words (McKenzie, D'Souza).
const properName = (s) =>
  String(s || '')
    .split(' ')
    .map((tok) => {
      const hasLower = /[a-z]/.test(tok)
      const hasUpper = /[A-Z]/.test(tok)
      if (hasLower && hasUpper) return tok
      return tok.replace(
        /[A-Za-z]+/g,
        (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
      )
    })
    .join(' ')

// Shrink-to-width heuristic (avg glyph ~0.55em) — mirrors the generator's
// measured fit without canvas text metrics.
const fitSize = (text, base, maxWidth) => {
  const n = (text || '').length
  if (!n) return base
  return Math.min(base, Math.round(maxWidth / (0.55 * n)))
}

const IdCardWave = () => {
  const pts = ['0,0', `${CARD_W},0`]
  CARD_WAVE.slice()
    .reverse()
    .forEach(([tx, ty]) => pts.push(`${tx * CARD_W},${ty * CARD_H}`))
  return (
    <svg
      width={CARD_W}
      height={CARD_H}
      viewBox={`0 0 ${CARD_W} ${CARD_H}`}
      style={{ position: 'absolute', left: 0, top: 0 }}
    >
      <polygon points={pts.join(' ')} fill={CARD_ORANGE} />
    </svg>
  )
}

// Name block: lowercase, first word demi-bold, rest regular on a second line.
// A lone trailing initial stays inline with the first name; a leading initial
// attaches to the first real word (from the generator's _name_block).
const isInitial = (w) => w.replace(/[^A-Za-z]/g, '').length <= 2

const cardNameBlock = (name) => {
  const toks = (name || '').trim().split(/\s+/).filter(Boolean)
  if (!toks.length) return { head: '', tail: '', inline: false }
  let head
  let tail
  if (toks.length >= 2 && isInitial(toks[0])) {
    head = `${toks[0]} ${toks[1]}`
    tail = toks.slice(2)
  } else {
    head = toks[0]
    tail = toks.slice(1)
  }
  const tailAllInit = tail.length > 0 && tail.every(isInitial)
  return {
    head: head.toLowerCase(),
    tail: tail.join(' ').toLowerCase(),
    inline: tailAllInit
  }
}

const IdCardFront = ({ member }) => {
  const r = Math.round(CARD_W * 0.33)
  const { head, tail, inline } = cardNameBlock(member.name)
  const single = !tail || inline
  const nameW = CARD_W * 0.86
  const headSize = fitSize(
    inline ? `${head} ${tail}` : head,
    Math.round(CARD_H * 0.0782),
    nameW
  )
  const tailSize = fitSize(tail, Math.round(CARD_H * 0.0782), nameW)
  const designation = member.cardTitle || member.role || ''
  const desigSize = fitSize(designation, Math.round(CARD_H * 0.0352), nameW)
  return (
    <div
      data-idcard
      style={{
        width: CARD_W,
        height: CARD_H,
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: '#fff',
        flexShrink: 0,
        fontFamily: 'AvenirRounded, Avenir, sans-serif'
      }}
    >
      <IdCardWave />
      {member.photo ? (
        <img
          src={member.photo}
          alt={member.name}
          style={{
            position: 'absolute',
            left: CARD_W / 2 - r,
            top: CARD_H * 0.245 - r,
            width: 2 * r,
            height: 2 * r,
            borderRadius: '50%',
            objectFit: 'cover',
            objectPosition: 'center 42%',
            border: `${Math.round(CARD_W * 0.0075)}px solid #fff`,
            boxSizing: 'border-box'
          }}
        />
      ) : (
        <div
          style={{
            position: 'absolute',
            left: CARD_W / 2 - r,
            top: CARD_H * 0.245 - r,
            width: 2 * r,
            height: 2 * r,
            borderRadius: '50%',
            backgroundColor: '#F0EDE8',
            border: `${Math.round(CARD_W * 0.0075)}px solid #fff`,
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#9B968E',
            fontSize: 20
          }}
        >
          photo goes here
        </div>
      )}
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: CARD_H * (single ? 0.56 : 0.52),
          textAlign: 'center',
          color: CARD_DARK,
          lineHeight: 1.06
        }}
      >
        {inline ? (
          <div style={{ fontSize: headSize }}>
            <span style={{ fontWeight: 600 }}>{head}</span>{' '}
            <span style={{ fontWeight: 400 }}>{tail}</span>
          </div>
        ) : (
          <>
            <div style={{ fontWeight: 600, fontSize: headSize }}>{head}</div>
            {tail ? (
              <div style={{ fontWeight: 400, fontSize: tailSize }}>{tail}</div>
            ) : null}
          </>
        )}
      </div>
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: CARD_H * 0.728,
          textAlign: 'center',
          color: '#000',
          fontWeight: 400,
          fontSize: desigSize
        }}
      >
        {designation}
      </div>
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: CARD_H * 0.781,
          textAlign: 'center',
          color: '#000',
          fontWeight: 400,
          fontSize: Math.round(CARD_H * 0.0352)
        }}
      >
        {member.phone}
      </div>
      <img
        src={ohWordmarkOrange}
        alt="openhouse"
        style={{
          position: 'absolute',
          left: CARD_W * 0.1775,
          top: CARD_H * 0.862,
          width: CARD_W * 0.645
        }}
      />
    </div>
  )
}

const IdCardBack = ({ member }) => {
  const mx = CARD_W * 0.111
  return (
    <div
      data-idcard
      style={{
        width: CARD_W,
        height: CARD_H,
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: CARD_ORANGE,
        flexShrink: 0,
        color: '#fff',
        fontFamily: 'AvenirRounded, Avenir, sans-serif'
      }}
    >
      <div
        style={{
          position: 'absolute',
          left: mx,
          top: CARD_H * 0.237,
          fontWeight: 600,
          fontSize: Math.round(CARD_H * 0.025)
        }}
      >
        Emergency Contact
      </div>
      {(member.emergencyName || '').trim() ? (
        <div
          style={{
            position: 'absolute',
            left: mx,
            top: CARD_H * 0.27,
            fontWeight: 400,
            fontSize: Math.round(CARD_H * 0.025)
          }}
        >
          {properName(member.emergencyName)}
        </div>
      ) : null}
      {(member.emergencyPhone || '').trim() ? (
        <div
          style={{
            position: 'absolute',
            left: mx,
            top: CARD_H * ((member.emergencyName || '').trim() ? 0.3 : 0.27),
            fontWeight: 400,
            fontSize: Math.round(CARD_H * 0.025)
          }}
        >
          {member.emergencyPhone}
        </div>
      ) : null}
      <div
        style={{
          position: 'absolute',
          left: mx,
          top: CARD_H * 0.365,
          width: CARD_W - 2 * mx,
          fontWeight: 400,
          fontSize: Math.round(CARD_H * 0.0215),
          lineHeight: `${Math.round(CARD_H * 0.0285)}px`,
          textAlign: 'justify'
        }}
      >
        {CARD_LEGAL}
      </div>
      <img
        src={ohMarkWhite}
        alt="oh."
        style={{
          position: 'absolute',
          left: CARD_W * 0.858 - CARD_W * 0.258,
          top: CARD_H * 0.62,
          width: CARD_W * 0.258
        }}
      />
    </div>
  )
}

// Category guess for people arriving from the ID form (no section there).
const guessSection = (title) => {
  const t = (title || '').toLowerCase()
  if (/housekeep|security|caretaker|support/.test(t)) return 'support'
  if (/director|coordinator|manager|admission|community/.test(t))
    return 'centre_team'
  return 'teachers'
}

export { CARD_W, CARD_H, CARD_MM, IdCardFront, IdCardBack, guessSection }
